/**
 * Pure helpers for Immo 360 → Rentalyzer analysis conversion ownership,
 * write attempt UI state, and post-write refresh planning.
 * Never replays conversion on refresh failure.
 */

/**
 * Apply a conversion success result only while still on the same prospect visit.
 * @param {{
 *   requestProspectId?: string | null,
 *   requestVisitId?: number | null,
 *   activeProspectId?: string | null,
 *   activeVisitId?: number | null,
 *   requestAuthUid?: string | null,
 *   activeAuthUid?: string | null,
 * }} params
 */
export function shouldApplyConversionResult({
  requestProspectId,
  requestVisitId,
  activeProspectId,
  activeVisitId,
  requestAuthUid = null,
  activeAuthUid = null,
} = {}) {
  const requestId = String(requestProspectId || '').trim();
  const activeId = String(activeProspectId || '').trim();
  if (!requestId || !activeId || requestId !== activeId) return false;
  if (requestVisitId != null && activeVisitId != null && requestVisitId !== activeVisitId) {
    return false;
  }
  if (requestAuthUid != null || activeAuthUid != null) {
    const reqUid = String(requestAuthUid || '').trim();
    const actUid = String(activeAuthUid || '').trim();
    if (!reqUid || !actUid || reqUid !== actUid) return false;
  }
  return true;
}

/**
 * Plan post-conversion refresh. Never schedules a write replay.
 * @param {{
 *   writeCommitted?: boolean,
 *   writeFailed?: boolean,
 *   refreshOk?: boolean | null,
 * }} params
 * @returns {{
 *   shouldRefresh: boolean,
 *   outcome: 'idle' | 'writeFailed' | 'writeCommittedRefreshPending' | 'writeCommittedRefreshOk' | 'writeCommittedRefreshFailed',
 *   replayWrite: false,
 * }}
 */
export function planPostConversionRefresh({
  writeCommitted = false,
  writeFailed = false,
  refreshOk = null,
} = {}) {
  if (writeFailed || !writeCommitted) {
    return { shouldRefresh: false, outcome: 'writeFailed', replayWrite: false };
  }
  if (refreshOk === true) {
    return { shouldRefresh: false, outcome: 'writeCommittedRefreshOk', replayWrite: false };
  }
  if (refreshOk === false) {
    return { shouldRefresh: false, outcome: 'writeCommittedRefreshFailed', replayWrite: false };
  }
  return { shouldRefresh: true, outcome: 'writeCommittedRefreshPending', replayWrite: false };
}

export function createConversionWriteState() {
  return {
    status: 'idle',
    writeCommitted: false,
    refreshFailed: false,
    errorKind: null,
    uncertainRetry: false,
    propertyId: null,
    prospect: null,
    mode: null,
    idempotent: false,
  };
}

export function beginConversionWrite(state) {
  return {
    ...(state || createConversionWriteState()),
    status: 'pending',
    writeCommitted: false,
    refreshFailed: false,
    errorKind: null,
    uncertainRetry: false,
  };
}

export function markConversionWriteSuccess(
  state,
  { propertyId = null, prospect = null, mode = null, idempotent = false } = {},
) {
  return {
    ...(state || createConversionWriteState()),
    status: 'success',
    writeCommitted: true,
    refreshFailed: false,
    errorKind: null,
    uncertainRetry: false,
    propertyId: propertyId || null,
    prospect: prospect || null,
    mode: mode || null,
    idempotent: idempotent === true,
  };
}

/**
 * @param {ReturnType<typeof createConversionWriteState>} state
 * @param {{ errorKind?: string | null, uncertainRetry?: boolean }} [opts]
 */
export function markConversionWriteFailed(state, { errorKind = null, uncertainRetry = false } = {}) {
  return {
    ...(state || createConversionWriteState()),
    status: 'writeFailed',
    writeCommitted: false,
    refreshFailed: false,
    errorKind: errorKind || null,
    uncertainRetry: uncertainRetry === true,
  };
}

/**
 * Post-write refresh failed. Write already committed — never replay conversion.
 * @param {ReturnType<typeof createConversionWriteState>} state
 */
export function markConversionRefreshFailed(state) {
  return {
    ...(state || createConversionWriteState()),
    status: 'writeCommittedRefreshFailed',
    writeCommitted: true,
    refreshFailed: true,
    uncertainRetry: false,
  };
}

/**
 * Whether a classified callable error is ambiguous enough to reuse a frozen write attempt.
 * @param {string | null | undefined} kind
 */
export function isConversionWriteUncertainError(kind) {
  return kind === 'unavailable' || kind === 'internal' || kind === 'error';
}

/**
 * Production create/link submit orchestration.
 * Distinguishes prepare (new request from current form) vs retry (exact frozen payload).
 * Current-form readiness is only required for prepare — not for retrying a frozen attempt.
 * Success / definitive-failure cleanup is scoped to the submitted operationId so a stale
 * completion cannot clear a newer session's frozen attempt on a shared manager.
 *
 * @param {{
 *   intent: 'prepare' | 'retry',
 *   writeAttempt: {
 *     prepareNewAttempt: Function,
 *     retryFrozenAttempt: Function,
 *     discard?: Function,
 *     discardIfOperation?: Function,
 *   },
 *   fingerprint?: string,
 *   build?: (operationId: string) => object,
 *   generateOperationId?: () => string,
 *   formReady?: boolean,
 *   submit: (payload: object) => Promise<object>,
 *   classifyError: (err: unknown) => string,
 * }} params
 */
export async function runConversionWriteAttempt({
  intent,
  writeAttempt,
  fingerprint,
  build,
  generateOperationId,
  formReady = true,
  submit,
  classifyError,
} = {}) {
  if (!writeAttempt || typeof submit !== 'function' || typeof classifyError !== 'function') {
    return { ok: false, reason: 'invalid_args', uncertainRetry: false, errorKind: null, payload: null };
  }

  let payload = null;

  if (intent === 'retry') {
    const retried = writeAttempt.retryFrozenAttempt();
    if (!retried.ok) {
      return {
        ok: false,
        reason: 'no_attempt',
        uncertainRetry: false,
        errorKind: null,
        payload: null,
      };
    }
    payload = retried.payload;
  } else {
    if (!formReady) {
      return {
        ok: false,
        reason: 'form_not_ready',
        uncertainRetry: false,
        errorKind: null,
        payload: null,
      };
    }
    if (typeof fingerprint !== 'string' || typeof build !== 'function') {
      return {
        ok: false,
        reason: 'invalid_prepare',
        uncertainRetry: false,
        errorKind: null,
        payload: null,
      };
    }
    const prepared = writeAttempt.prepareNewAttempt({
      fingerprint,
      build,
      generateOperationId,
    });
    if (!prepared.ok) {
      return {
        ok: false,
        reason: 'attempt_locked',
        uncertainRetry: true,
        errorKind: null,
        payload: prepared.payload,
      };
    }
    payload = prepared.payload;
  }

  const submittedOperationId =
    payload?.operationId == null ? '' : String(payload.operationId).trim();

  const cleanupSubmittedAttempt = () => {
    if (!submittedOperationId) return;
    if (typeof writeAttempt.discardIfOperation === 'function') {
      writeAttempt.discardIfOperation(submittedOperationId);
      return;
    }
    // Legacy managers without operation-scoped cleanup: only discard when still the same op.
    const current = writeAttempt.getAttemptPayload?.();
    if (current && String(current.operationId || '').trim() === submittedOperationId) {
      writeAttempt.discard?.();
    }
  };

  try {
    const result = await submit(payload);
    cleanupSubmittedAttempt();
    return {
      ok: true,
      result,
      payload,
      uncertainRetry: false,
      errorKind: null,
    };
  } catch (err) {
    const errorKind = classifyError(err);
    const uncertainRetry = isConversionWriteUncertainError(errorKind);
    if (!uncertainRetry) {
      cleanupSubmittedAttempt();
    }
    return {
      ok: false,
      reason: 'submit_failed',
      error: err,
      errorKind,
      uncertainRetry,
      payload,
    };
  }
}
